/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import { domUtils } from '@ba-ui-toolkit/utils';

const { getNearestPositionedParent } = domUtils;

// Commonly used variables
const invalidSpecs = { top: 0, left: 0, placement: 'right', invalid: true };
const shiftArrow = 23;

// Determines base top/left value to use, based off of four situations: Rendered normally (without portal) where trigger has position static,
// rendered normally where trigger has position other than static, added to domNode with position static, or added to domNode with position other than static
function getStartingOffset(
	positionedAncestorLocation,
	triggerNode,
	triggerRectOffset,
	nearestRectOffset,
	scrollTopOffset,
	domNodeToAttachTo,
	domNodeToAttachToRectOffset
) {
	if (
		domNodeToAttachToRectOffset &&
		(window.getComputedStyle(domNodeToAttachTo).getPropertyValue('position') === 'static' ||
			window.getComputedStyle(domNodeToAttachTo).getPropertyValue('position') === 'relative')
	) {
		return positionedAncestorLocation
			? triggerRectOffset - positionedAncestorLocation
			: triggerRectOffset + scrollTopOffset;
	} else if (typeof domNodeToAttachToRectOffset !== 'undefined') {
		return triggerRectOffset - domNodeToAttachToRectOffset - domNodeToAttachTo.clientTop;
	} else if (
		window.getComputedStyle(triggerNode).getPropertyValue('position') === 'static' ||
		window.getComputedStyle(triggerNode).getPropertyValue('position') === 'relative'
	) {
		return triggerRectOffset - nearestRectOffset;
	}

	return 0;
}

function validateSpace(orientation, startingSpace, containerRect, triggerRect, options) {
	switch (orientation) {
		case 'top':
			startingSpace -= containerRect.height;
			if (options.arrow) startingSpace -= options.arrow.getBoundingClientRect().height;
			if (typeof options.offset !== 'undefined') startingSpace -= options.offset;

			return startingSpace;
		case 'bottom':
			startingSpace += triggerRect.height;
			startingSpace += containerRect.height;
			if (options.arrow) startingSpace += options.arrow.getBoundingClientRect().height;
			if (typeof options.offset !== 'undefined') startingSpace += options.offset;

			return startingSpace;
		case 'left':
			startingSpace -= containerRect.width;
			if (options.arrow) startingSpace -= options.arrow.getBoundingClientRect().width;
			if (typeof options.offset !== 'undefined') startingSpace -= options.offset;

			return startingSpace;
		case 'right':
			startingSpace += triggerRect.width;
			startingSpace += containerRect.width;
			if (options.arrow) startingSpace += options.arrow.getBoundingClientRect().width;
			if (typeof options.offset !== 'undefined') startingSpace += options.offset;

			return startingSpace;
		default:
			return startingSpace;
	}
}

export function getValidPlacement(
	flyoutTrigger,
	flyoutContainer,
	placement,
	candidatePlacement,
	options
) {
	const containerRect = flyoutContainer.getBoundingClientRect();
	const triggerRect = flyoutTrigger.getBoundingClientRect();
	let arrowRect;
	if (options.arrow) arrowRect = options.arrow.getBoundingClientRect();
	const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	// default initialization is for window
	let startingTop = triggerRect.top;
	let startingLeft = triggerRect.left;
	let heightToCheck = window.innerHeight;
	let widthToCheck = window.innerWidth;
	if (options.body === 'page') {
		const { body } = document;
		const html = document.documentElement;
		const pageHeight = Math.max(
			body.scrollHeight,
			body.offsetHeight,
			html.clientHeight,
			html.offsetHeight
		);
		const pageWidth = Math.max(
			body.scrollWidth,
			body.offsetWidth,
			html.clientWidth,
			html.offsetWidth
		);
		startingTop = triggerRect.top + scrollTop;
		startingLeft = triggerRect.left + scrollLeft;
		heightToCheck = pageHeight;
		widthToCheck = pageWidth;
	}

	let result = { top: 0, left: 0, placement, invalid: true };
	let resultTop;
	let resultLeft;
	let domNodeToAttachToRect;
	let positionedAncestor = { top: 0, left: 0 };
	if (options.domNodeToAttachTo) {
		domNodeToAttachToRect = options.domNodeToAttachTo.getBoundingClientRect();
		if (options.domNodeToAttachTo.offsetParent) {
			positionedAncestor = options.domNodeToAttachTo.offsetParent.getBoundingClientRect();
		}
	}
	const nearestPositionedElement = getNearestPositionedParent(flyoutTrigger);
	const nearestRect = nearestPositionedElement.getBoundingClientRect();
	// above is for result initialization

	const placementToValidate = candidatePlacement.slice();
	let index;

	while (result.invalid && placementToValidate.length >= 0) {
		let checkTop = startingTop;
		let checkLeft = startingLeft;
		const extraH = (containerRect.width - triggerRect.width) / 2;
		const extraV = (containerRect.height - triggerRect.height) / 2;
		switch (placement) {
			case 'top':
				checkTop = validateSpace('top', startingTop, containerRect, triggerRect, options);
				checkLeft += triggerRect.width - containerRect.width;
				checkLeft -= triggerRect.width;
				checkLeft += shiftArrow;
				if ((checkTop > 0 && checkLeft > 0) || options.final === true) {
					// Do the calculation here
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) - containerRect.height;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					resultLeft -= containerRect.width;
					resultLeft -= triggerRect.width;
					resultLeft += shiftArrow;
					if (arrowRect) resultTop -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					// remove placement that is already validated
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					// get another placement to check
					placement = placementToValidate[0];
				}
				break;
			case 'topLeft':
				checkTop = validateSpace('top', startingTop, containerRect, triggerRect, options);
				checkLeft += triggerRect.width - containerRect.width;
				checkLeft -= triggerRect.width;
				checkLeft += shiftArrow;
				if ((checkTop > 0 && checkLeft > 0) || options.final === true) {
					// Do the calculation here
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) - containerRect.height;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					resultLeft -= containerRect.width;
					resultLeft -= triggerRect.width;
					resultLeft += shiftArrow;
					if (arrowRect) resultTop -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					// remove placement that is already validated
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					// get another placement to check
					placement = placementToValidate[0];
				}
				break;
			case 'topRight':
				checkTop = validateSpace('top', startingTop, containerRect, triggerRect, options);
				checkLeft += containerRect.width;
				checkLeft += triggerRect.width;
				checkLeft -= shiftArrow;
				if ((checkTop > 0 && checkLeft < widthToCheck) || options.final === true) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) - containerRect.height;
					resultLeft = getStartingOffset(
						positionedAncestor.left,
						flyoutTrigger,
						triggerRect.left,
						nearestRect.left,
						scrollLeft,
						options.domNodeToAttachTo,
						domNodeToAttachToRect && domNodeToAttachToRect.left
					);
					resultLeft += triggerRect.width;
					resultLeft -= shiftArrow;
					if (arrowRect) resultTop -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'topCentred':
				checkTop = validateSpace('top', startingTop, containerRect, triggerRect, options);
				if (
					(checkTop > 0 &&
						checkLeft - extraH > 0 &&
						checkLeft + triggerRect.width + extraH < widthToCheck) ||
					options.final === true
				) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) - containerRect.height;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) +
						triggerRect.width / 2;
					resultLeft -= containerRect.width / 2;
					if (arrowRect) resultTop -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'bottom':
				checkTop = validateSpace(
					'bottom',
					startingTop,
					containerRect,
					triggerRect,
					options
				);
				checkLeft += triggerRect.width - containerRect.width;
				checkLeft -= triggerRect.width;
				checkLeft += shiftArrow;
				if ((heightToCheck > checkTop && checkLeft > 0) || options.final === true) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) + triggerRect.height;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					resultLeft -= containerRect.width;
					resultLeft -= triggerRect.width;
					resultLeft += shiftArrow;
					if (arrowRect) resultTop += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'bottomLeft':
				checkTop = validateSpace(
					'bottom',
					startingTop,
					containerRect,
					triggerRect,
					options
				);
				checkLeft += triggerRect.width - containerRect.width;
				checkLeft -= triggerRect.width;
				checkLeft += shiftArrow;
				if ((heightToCheck > checkTop && checkLeft > 0) || options.final === true) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) + triggerRect.height;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					resultLeft -= containerRect.width;
					resultLeft -= triggerRect.width;
					resultLeft += shiftArrow;
					if (arrowRect) resultTop += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'bottomRight':
				checkTop = validateSpace(
					'bottom',
					startingTop,
					containerRect,
					triggerRect,
					options
				);
				checkLeft += containerRect.width;
				checkLeft += triggerRect.width;
				checkLeft -= shiftArrow;
				if (
					(heightToCheck > checkTop && checkLeft < widthToCheck) ||
					options.final === true
				) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) + triggerRect.height;
					resultLeft = getStartingOffset(
						positionedAncestor.left,
						flyoutTrigger,
						triggerRect.left,
						nearestRect.left,
						scrollLeft,
						options.domNodeToAttachTo,
						domNodeToAttachToRect && domNodeToAttachToRect.left
					);
					resultLeft += triggerRect.width;
					resultLeft -= shiftArrow;
					if (arrowRect) resultTop += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'bottomCentred':
				checkTop = validateSpace(
					'bottom',
					startingTop,
					containerRect,
					triggerRect,
					options
				);
				if (
					(heightToCheck > checkTop &&
						checkLeft - extraH > 0 &&
						checkLeft + triggerRect.width + extraH < widthToCheck) ||
					options.final === true
				) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) + triggerRect.height;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) +
						triggerRect.width / 2;
					resultLeft -= containerRect.width / 2;
					if (arrowRect) resultTop += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultTop += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'left':
				checkLeft = validateSpace(
					'left',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				checkTop += triggerRect.height;
				checkTop -= shiftArrow;
				checkTop += containerRect.height;
				if ((checkLeft > 0 && heightToCheck > checkTop) || options.final === true) {
					resultTop = getStartingOffset(
						positionedAncestor.top,
						flyoutTrigger,
						triggerRect.top,
						nearestRect.top,
						scrollTop,
						options.domNodeToAttachTo,
						domNodeToAttachToRect && domNodeToAttachToRect.top
					);
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) - containerRect.width;
					resultTop += triggerRect.height;
					resultTop -= shiftArrow;
					if (arrowRect) resultLeft -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'leftBottom':
				checkLeft = validateSpace(
					'left',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				checkTop += triggerRect.height;
				checkTop -= shiftArrow;
				checkTop += containerRect.height;
				if ((checkLeft > 0 && heightToCheck > checkTop) || options.final === true) {
					resultTop = getStartingOffset(
						positionedAncestor.top,
						flyoutTrigger,
						triggerRect.top,
						nearestRect.top,
						scrollTop,
						options.domNodeToAttachTo,
						domNodeToAttachToRect && domNodeToAttachToRect.top
					);
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) - containerRect.width;
					resultTop += triggerRect.height;
					resultTop -= shiftArrow;
					if (arrowRect) resultLeft -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'leftTop':
				checkLeft = validateSpace(
					'left',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				checkTop -= containerRect.height - triggerRect.height;
				checkTop -= triggerRect.height;
				checkTop += shiftArrow;
				if ((checkLeft > 0 && checkTop > 0) || options.final === true) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) - containerRect.height;
					resultTop += shiftArrow;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) - containerRect.width;
					if (arrowRect) resultLeft -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'leftCentred':
				checkLeft = validateSpace(
					'left',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				if (
					(checkLeft > 0 &&
						checkTop - extraV > 0 &&
						checkTop + triggerRect.height + extraV < heightToCheck) ||
					options.final === true
				) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) -
						containerRect.height / 2;
					resultTop += triggerRect.height / 2;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) - containerRect.width;
					if (arrowRect) resultLeft -= Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft -= options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'right':
				checkLeft = validateSpace(
					'right',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				checkTop += triggerRect.height;
				checkTop -= shiftArrow;
				checkTop += containerRect.height;
				if (
					(widthToCheck > checkLeft && heightToCheck > checkTop) ||
					options.final === true
				) {
					resultTop = getStartingOffset(
						positionedAncestor.top,
						flyoutTrigger,
						triggerRect.top,
						nearestRect.top,
						scrollTop,
						options.domNodeToAttachTo,
						domNodeToAttachToRect && domNodeToAttachToRect.top
					);
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					resultTop += triggerRect.height;
					resultTop -= shiftArrow;
					if (arrowRect) resultLeft += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'rightBottom':
				checkLeft = validateSpace(
					'right',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				checkTop += triggerRect.height;
				checkTop -= shiftArrow;
				checkTop += containerRect.height;
				if (
					(widthToCheck > checkLeft && heightToCheck > checkTop) ||
					options.final === true
				) {
					resultTop = getStartingOffset(
						positionedAncestor.top,
						flyoutTrigger,
						triggerRect.top,
						nearestRect.top,
						scrollTop,
						options.domNodeToAttachTo,
						domNodeToAttachToRect && domNodeToAttachToRect.top
					);
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					resultTop += triggerRect.height;
					resultTop -= shiftArrow;
					if (arrowRect) resultLeft += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'rightTop':
				checkLeft = validateSpace(
					'right',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				checkTop -= containerRect.height - triggerRect.height;
				checkTop -= triggerRect.height;
				checkTop += shiftArrow;
				if ((widthToCheck > checkLeft && checkTop > 0) || options.final === true) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) - containerRect.height;
					resultTop += shiftArrow;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					if (arrowRect) resultLeft += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			case 'rightCentred':
				checkLeft = validateSpace(
					'right',
					startingLeft,
					containerRect,
					triggerRect,
					options
				);
				if (
					(widthToCheck > checkLeft &&
						checkTop - extraV > 0 &&
						checkTop + triggerRect.height + extraV < heightToCheck) ||
					options.final === true
				) {
					resultTop =
						getStartingOffset(
							positionedAncestor.top,
							flyoutTrigger,
							triggerRect.top,
							nearestRect.top,
							scrollTop,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.top
						) -
						containerRect.height / 2;
					resultTop += triggerRect.height / 2;
					resultLeft =
						getStartingOffset(
							positionedAncestor.left,
							flyoutTrigger,
							triggerRect.left,
							nearestRect.left,
							scrollLeft,
							options.domNodeToAttachTo,
							domNodeToAttachToRect && domNodeToAttachToRect.left
						) + triggerRect.width;
					if (arrowRect) resultLeft += Math.min(arrowRect.height, arrowRect.width);
					if (typeof options.offset !== 'undefined') resultLeft += options.offset;
					result = {
						top: Math.round(resultTop),
						left: Math.round(resultLeft),
						placement,
						invalid: false,
					};
				} else {
					index = placementToValidate.indexOf(placement);
					placementToValidate.splice(index, 1);
					placement = placementToValidate[0];
				}
				break;
			default:
				return { top: 0, left: 0, placement, invalid: true };
		}
	}

	return result;
}

// Determines the position for flyout, while finding a valid placement and checking if there is space to place the flyout
export default function getSpecs(
	flyoutTrigger,
	flyoutContainer,
	placement,
	options,
	candidatePlacement
) {
	if (
		flyoutTrigger.getBoundingClientRect === undefined ||
		flyoutContainer.getBoundingClientRect === undefined
	) {
		return invalidSpecs;
	}
	// Check if a valid placement that fits in the current window the page exists
	options.body = 'window';
	let validPlacement = getValidPlacement(
		flyoutTrigger,
		flyoutContainer,
		placement,
		candidatePlacement,
		options
	);

	// If no valid placement fits in the current window, check if there exists a valid placement on the page (i.e. will not be fully visible in the window, but will fit on the page)
	if (validPlacement.invalid) {
		options.body = 'page';
		validPlacement = getValidPlacement(
			flyoutTrigger,
			flyoutContainer,
			placement,
			candidatePlacement,
			options
		);
	}

	// if no valid placement anyway, display the flyout directly
	if (validPlacement.invalid) {
		options.final = true;
		validPlacement = getValidPlacement(
			flyoutTrigger,
			flyoutContainer,
			placement,
			candidatePlacement,
			options
		);
	}

	return validPlacement;
}
